import {server} from "@reactivated";

// #1810 pull-to-refresh diagnostic. Everything here runs outside React:
// window-level capture-phase listeners, installed once, so every sample is
// taken before any synthetic handler or render the gesture causes, and
// nothing here can re-render or mutate the DOM. Reports ride apple_ping
// (log=True), landing in core_rpcrequest under input->type = "PTR_PROBE".

const REPORT_MIN_INTERVAL_MS = 250;
const SETTLE_DELAY_MS = 700;
const SCROLL_DEBOUNCE_MS = 400;

export const install = (deviceUUID: string) => {
    let lastReportAt = 0;
    let fingerDown = false;
    let lastTouchEndAt = 0;
    let topBeforeMove: number | null = null;
    let settleTimer: number | undefined;
    let scrollTimer: number | undefined;

    const sample = (ev: string, extra: Record<string, number | null> = {}) => {
        const now = Date.now();
        if (now - lastReportAt < REPORT_MIN_INTERVAL_MS) {
            return;
        }
        lastReportAt = now;
        const scroller = document.getElementById("app-scroller");
        void server.core.rpc.apple_ping({
            device_id: deviceUUID,
            type: "PTR_PROBE",
            data: {
                ev,
                top: scroller?.scrollTop ?? null,
                sh: scroller?.scrollHeight ?? null,
                ch: scroller?.clientHeight ?? null,
                path: window.location.pathname,
                ...extra,
            },
        });
    };

    window.addEventListener(
        "touchstart",
        (e) => {
            if (e.touches.length !== 1) {
                return;
            }
            fingerDown = true;
            sample("touch");
        },
        {capture: true, passive: true},
    );

    const onTouchDone = (e: TouchEvent) => {
        if (e.touches.length > 0) {
            return;
        }
        fingerDown = false;
        lastTouchEndAt = Date.now();
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => sample("settle"), SETTLE_DELAY_MS);
    };
    window.addEventListener("touchend", onTouchDone, {capture: true, passive: true});
    window.addEventListener("touchcancel", onTouchDone, {
        capture: true,
        passive: true,
    });

    // scroll does not bubble, but the capture phase still descends through
    // window — this hears #app-scroller's own scroll events without ever
    // touching the element.
    window.addEventListener(
        "scroll",
        (e) => {
            if (!(e.target instanceof HTMLElement) || e.target.id !== "app-scroller") {
                return;
            }
            if (fingerDown) {
                topBeforeMove = null;
                return;
            }
            topBeforeMove = topBeforeMove ?? e.target.scrollTop;
            window.clearTimeout(scrollTimer);
            scrollTimer = window.setTimeout(() => {
                // Fingerless movement, now settled: momentum when dt_touch is
                // small; anchoring or programmatic when it is not.
                sample("moved", {
                    from: topBeforeMove,
                    dt_touch: Date.now() - lastTouchEndAt,
                });
                topBeforeMove = null;
            }, SCROLL_DEBOUNCE_MS);
        },
        {capture: true, passive: true},
    );

    sample("install");
};
